import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import SearchResults from './components/SearchResults';
import TrendingPanel from './components/TrendingPanel';
import ProvenancePane from './components/ProvenancePane';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PublicClaimSearch = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [provenanceUrl, setProvenanceUrl] = useState(null);
  
  const [filters, setFilters] = useState({
    claimType: 'all',
    verdictStatus: 'all',
    country: 'all',
    dateRange: 'all',
    efcsnVerified: false,
    highConfidence: false,
    hasEvidence: false
  });

  const [searchResults, setSearchResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);

  // Mock search results data
  const mockResults = [
    {
      id: 1,
      title: "EU Climate Policy Will Reduce Carbon Emissions by 55% by 2030",
      summary: "Claims about the European Union's climate policy targets and their effectiveness in reducing carbon emissions across member states. The policy includes various measures such as carbon pricing, renewable energy targets, and industrial regulations.",
      category: "Climate & Environment",
      publishedDate: "2024-09-18",
      verdict: "mostly-true",
      confidenceScore: 82,
      efcsnVerified: true,
      hasEvidence: true,
      hasC2PA: true,
      crossReferenced: true,
      reviewerCount: 5,
      sourcePreview: {
        title: "European Commission Climate Action Plan",
        domain: "ec.europa.eu",
        favicon: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=32&h=32&fit=crop&crop=center"
      }
    },
    {
      id: 2,
      title: "Healthcare Spending in Nordic Countries Exceeds EU Average",
      summary: "Statistical claims regarding healthcare expenditure per capita in Nordic EU member states compared to the European Union average. Analysis includes both public and private healthcare spending data from official sources.",
      category: "Health & Medicine",
      publishedDate: "2024-09-17",
      verdict: "true",
      confidenceScore: 94,
      efcsnVerified: true,
      hasEvidence: true,
      hasC2PA: false,
      crossReferenced: true,
      reviewerCount: 8,
      sourcePreview: {
        title: "OECD Health Statistics 2024",
        domain: "oecd.org",
        favicon: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=32&h=32&fit=crop&crop=center"
      }
    },
    {
      id: 3,
      title: "Social Media Platforms Remove 40% More Misinformation Since DSA",
      summary: "Claims about the effectiveness of the Digital Services Act in combating misinformation on major social media platforms operating in the European Union. Data includes removal rates and policy enforcement statistics.",
      category: "Technology & Science",
      publishedDate: "2024-09-16",
      verdict: "mixed",
      confidenceScore: 67,
      efcsnVerified: false,
      hasEvidence: true,
      hasC2PA: false,
      crossReferenced: true,
      reviewerCount: 3,
      sourcePreview: {
        title: "Digital Services Act Impact Report",
        domain: "digitalservicesact.eu",
        favicon: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=32&h=32&fit=crop&crop=center"
      }
    },
    {
      id: 4,
      title: "Immigration to EU Countries Decreased by 15% in 2024",
      summary: "Statistical claims about immigration patterns and trends across European Union member states during 2024. Includes data on both legal immigration processes and asylum applications from various regions.",
      category: "Politics & Government",
      publishedDate: "2024-09-15",
      verdict: "false",
      confidenceScore: 23,
      efcsnVerified: true,
      hasEvidence: true,
      hasC2PA: false,
      crossReferenced: true,
      reviewerCount: 6,
      sourcePreview: {
        title: "Eurostat Migration Statistics",
        domain: "eurostat.ec.europa.eu",
        favicon: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=32&h=32&fit=crop&crop=center"
      }
    },
    {
      id: 5,
      title: "Renewable Energy Sources Now Provide 60% of EU Electricity",
      summary: "Claims about the current contribution of renewable energy sources to the European Union's electricity generation mix. Includes wind, solar, hydroelectric, and other renewable energy technologies across member states.",
      category: "Climate & Environment",
      publishedDate: "2024-09-14",
      verdict: "mostly-false",
      confidenceScore: 34,
      efcsnVerified: false,
      hasEvidence: false,
      hasC2PA: false,
      crossReferenced: false,
      reviewerCount: 2,
      sourcePreview: {
        title: "EU Energy Market Report",
        domain: "energy.ec.europa.eu",
        favicon: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=32&h=32&fit=crop&crop=center"
      }
    }
  ];

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      let filteredResults = mockResults?.filter(result => 
        result?.title?.toLowerCase()?.includes(query?.toLowerCase()) ||
        result?.summary?.toLowerCase()?.includes(query?.toLowerCase()) ||
        result?.category?.toLowerCase()?.includes(query?.toLowerCase())
      );
      
      setSearchResults(filteredResults);
      setTotalResults(filteredResults?.length);
      setLoading(false);
    }, 1000);
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    
    // Apply filters to results
    let filteredResults = [...mockResults];
    
    if (newFilters?.verdictStatus !== 'all') {
      filteredResults = filteredResults?.filter(result => result?.verdict === newFilters?.verdictStatus);
    }
    
    if (newFilters?.efcsnVerified) {
      filteredResults = filteredResults?.filter(result => result?.efcsnVerified);
    }
    
    if (newFilters?.highConfidence) {
      filteredResults = filteredResults?.filter(result => result?.confidenceScore >= 80);
    }
    
    if (newFilters?.hasEvidence) {
      filteredResults = filteredResults?.filter(result => result?.hasEvidence);
    }
    
    setSearchResults(filteredResults);
    setTotalResults(filteredResults?.length);
  };

  // Handle paste detection for provenance pane
  useEffect(() => {
    const handlePaste = (e) => {
      const pastedText = e?.clipboardData?.getData('text');
      try {
        new URL(pastedText);
        setProvenanceUrl(pastedText);
      } catch {
        // Not a valid URL, ignore
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Load initial trending results
  useEffect(() => {
    setSearchResults(mockResults?.slice(0, 3));
    setTotalResults(mockResults?.length);
  }, []);

  return (
    <>
      <Helmet>
        <title>Public Claim Search - TruthGuard EU</title>
        <meta name="description" content="Search and verify claims across the European Union. Access fact-checked information with confidence scores and EFCSN verification." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />

        <main className={`
          transition-all duration-300 pt-16
          ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        `}>
          <div className="container mx-auto px-4 py-8">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl">
                  <Icon name="Search" size={32} className="text-primary" />
                </div>
              </div>
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Verify Claims Across Europe
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Search our comprehensive database of fact-checked claims from across the EU. 
                Get instant verification with confidence scores and expert analysis.
              </p>

              {/* Search Bar */}
              <SearchBar
                onSearch={handleSearch}
                onFilterToggle={() => setShowFilters(!showFilters)}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
              />
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Search Results */}
              <div className="lg:col-span-3">
                <SearchResults
                  results={searchResults}
                  loading={loading}
                  searchQuery={searchQuery}
                  totalResults={totalResults}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>

              {/* Sidebar Content */}
              <div className="lg:col-span-1">
                <TrendingPanel />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-16 bg-card border border-border rounded-lg p-8">
              <h3 className="text-xl font-semibold text-foreground mb-6 text-center">
                Need Help Verifying Information?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  iconName="BookOpen"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    // Navigate to help
                  }}
                >
                  Verification Guide
                </Button>
                <Button
                  variant="outline"
                  iconName="MessageCircle"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    // Navigate to contact
                  }}
                >
                  Ask Experts
                </Button>
                <Button
                  variant="outline"
                  iconName="Share"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    // Share functionality
                  }}
                >
                  Share Results
                </Button>
              </div>
            </div>
          </div>
        </main>

        {/* Filter Panel */}
        <FilterPanel
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          onFiltersChange={handleFiltersChange}
        />

        {/* Provenance Pane */}
        {provenanceUrl && (
          <ProvenancePane
            url={provenanceUrl}
            onClose={() => setProvenanceUrl(null)}
          />
        )}
      </div>
    </>
  );
};

export default PublicClaimSearch;